import { computed, toValue } from 'vue'

import { storeToRefs } from 'pinia'

import { useAuth } from '@common/auth-layer/composables/useAuth'

import { useGlobal } from '@common/core-layer/composables/useGlobal'

import { useLocationStore } from '@common/location-layer/store/location.store'

import { useAccountSettingsTabVisibilityQueryStore } from '../stores/account-settings-tab-visibility.query-store'

import { ACCOUNT_SETTINGS_TABS, filterVisibleAccountSettingsTabs, getOrderedTabs, getValidTabIds } from '../utils/account-settings-config'
import type { TabVisibilityContext } from '../types/account-settings-layout.types'

/**
 * Builds {@link TabVisibilityContext} and derived visible tab lists for account settings
 * (desktop rail, mobile menu, content). See `ACCOUNT_SETTINGS_TABS[].shouldShowTab` in `account-settings-config.ts`.
 */
export function useAccountSettingsTabVisibility() {
  const auth = useAuth()
  const contactId = computed(() => auth.store.contactId)
  const { isGokollab, isClientportal, isMobileOrTablet } = useGlobal()
  const locationStore = useLocationStore()
  const { locationPermissions } = storeToRefs(locationStore)
  const tabVisibilityQueryStore = useAccountSettingsTabVisibilityQueryStore()

  const isGroupAdminOrOwnerQuery = tabVisibilityQueryStore.fetchIsGroupAdminOrOwnerQuery(contactId)

  /**
   * True when the group-admin permission check is safe to use for deny/redirect decisions.
   * While false, consumers must not treat a missing Meeting tab in `validVisibleTabIds` as denial.
   */
  const isGroupAdminPermissionSettled = computed(() => !contactId.value || isGroupAdminOrOwnerQuery.isFetched.value)

  const tabVisibilityContext = computed<TabVisibilityContext>(() => ({
    isGokollab: toValue(isGokollab),
    isClientportal: toValue(isClientportal),
    isMobile: isMobileOrTablet(),
    locationPermissions: { ...locationPermissions.value },
    isGroupAdminOrOwner: isGroupAdminOrOwnerQuery.data.value === true,
  }))

  const showMeetingTab = computed(() => tabVisibilityContext.value.isGroupAdminOrOwner)

  const visibleTabs = computed(() => filterVisibleAccountSettingsTabs(ACCOUNT_SETTINGS_TABS, tabVisibilityContext.value))

  const orderedVisibleTabs = computed(() => getOrderedTabs(visibleTabs.value))

  const validVisibleTabIds = computed(() => getValidTabIds(visibleTabs.value))

  return {
    isGroupAdminOrOwnerQuery,
    isGroupAdminPermissionSettled,
    tabVisibilityContext,
    showMeetingTab,
    visibleTabs,
    orderedVisibleTabs,
    validVisibleTabIds,
  }
}
